import React from "react";
import ReactECharts from "echarts-for-react";
import { groupSeriesXY, toTimestamp, formatDateFr } from "../utils/echartsSeries.js";
import { YEAR_COLORS } from "../constants.js";

// Ordre canonique des horizons (RCP classique ou TRACC).
const ORDRE_HORIZON = {
  2020: 0, reference: 0,
  2035: 1, "+2": 1,
  2050: 2, "+2.7": 2,
};

// Motif de trait SVG (légende) déduit du lineStyle.type d'ECharts, avec la même
// règle que ECharts : le motif n'est à maintenir qu'une seule fois, dans STATS.
export function motifSvg(type, epaisseur = 2) {
  if (type === "solid") return "";
  if (type === "dashed") return `${4 * epaisseur} ${2 * epaisseur}`;
  if (type === "dotted") return `${epaisseur} ${epaisseur}`;
  if (typeof type === "number") return `${type} ${type}`;
  return type.join(" ");
}

/**
 * Les statistiques tracées. Chaque année garde sa couleur (YEAR_COLORS) ;
 * c'est le style de trait qui distingue la statistique.
 *   echarts  : valeur de lineStyle.type (la légende en déduit son motif)
 */
export const STATS = [
  { cle: "moy", label: "Moyen", echarts: "solid" },
  { cle: "q75", label: "Quantile 75", echarts: [10, 2, 4, 2] },
  { cle: "q90", label: "Quantile 90", echarts: 3 },
  { cle: "max", label: "Maximum", echarts: "dotted" },
];

// Statistiques reprises dans le tableau par défaut, pour chaque année.
const STATS_TABLEAU_DEFAUT = ["q75", "q90"];

const libelleAnnee = (g) => (g === "reference" ? "Référence" : String(g));

function valueAtDate(points, targetTime) {
  if (points.length === 0) return null;
  const first = new Date(points[0].date).getTime();
  const last = new Date(points[points.length - 1].date).getTime();
  if (targetTime < first || targetTime > last) return null;
  for (let i = 0; i < points.length - 1; i++) {
    const t1 = new Date(points[i].date).getTime();
    const t2 = new Date(points[i + 1].date).getTime();
    if (targetTime >= t1 && targetTime <= t2) {
      const frac = t2 === t1 ? 0 : (targetTime - t1) / (t2 - t1);
      return points[i].valeur + frac * (points[i + 1].valeur - points[i].valeur);
    }
  }
  return null;
}

function formatMonthLabel(monthIndex) {
  return `01/${String(monthIndex + 1).padStart(2, "0")}`;
}

/** Pastille cliquable de légende (active = en couleur, sinon grisée).
 * Exportée pour que TensionChart ait exactement les mêmes pastilles. */
export function Puce({ actif, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={actif}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        fontSize: 14,
        color: actif ? "#000" : "#9aa0a6",
        background: "#fff",
        border: `1px solid ${actif ? "#555" : "#d0d4d9"}`,
        borderRadius: 14,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

/**
 * Graphique à 12 courbes (3 horizons x 4 statistiques) + tableau d'écarts.
 *
 * Deux sélecteurs de légende indépendants : un sur les années, un sur la
 * statistique. Une courbe est visible si son année ET sa statistique sont
 * cochées. Pas de bande d'écart-type.
 *
 * @param data            lignes de l'API (une par horizon et par jour)
 * @param prefixe         "besoin" (eau) ou "irr" (irrigation) : les champs
 *                        lus sont `${prefixe}_moy`, `_q75`, `_q90`, `_max`
 * @param cleDate         champ date des lignes (`date_affichage`, dans
 *                        l'eau comme dans l'irrigation)
 * @param unite           unité affichée (axe, tooltip, en-tête du tableau)
 * @param nomAxe          titre de l'axe Y
 * @param statsParDefaut  statistiques cochées à l'ouverture
 * @param statsTableau    statistiques du tableau, pour chaque année
 *                        (Quantile 75 et Quantile 90 par défaut)
 * @param tableauEnEcart  true : dans le tableau, les années autres que la base
 *                        affichent l'écart EN VALEUR avec le Moyen de l'année
 *                        de base (q75_2035 - moyen_2020...) au lieu d'une
 *                        variation en %
 *                        (la colonne de l'année de base passe alors à 1 décimale)
 * @param statsDisponibles statistiques proposées dans le sélecteur (toutes par
 *                        défaut)
 * @param ecartAvecBase   true : on trace, pour chaque statistique, l'écart
 *                        avec la MÊME statistique de l'année de base
 *                        (q75_2035 - q75_2020...). L'année de base n'est alors
 *                        ni tracée ni proposée dans le sélecteur.
 * @param afficherTableau false : pas de tableau d'écarts à droite
 * @param reserverPlaceTableau true : sans tableau, on garde sa place (zone vide
 *                        à droite) pour que le graphique ait la même largeur
 *                        que ceux de la même page qui en ont un
 * @param selecteurAnnees false : pas de sélecteur d'années (une seule année)
 * @param cumul           true : on trace le cumul de chaque statistique
 * @param xMax            borne haute de l'axe X (timestamp), optionnelle
 * @param yMinAuto        true : l'axe Y démarre au minimum réel des données
 *                        (arrondi à l'entier inférieur) au lieu de 0
 */
export default function CourbesStatChart({
  data,
  prefixe,
  cleDate,
  unite,
  nomAxe,
  statsParDefaut,
  statsTableau: STATS_TABLEAU = STATS_TABLEAU_DEFAUT,
  cumul = false,
  afficherTableau = true,
  reserverPlaceTableau = false,
  tableauEnEcart = false,
  statsDisponibles = STATS.map((st) => st.cle),
  ecartAvecBase = false,
  selecteurAnnees = true,
  xMax,
  yMinAuto = false,
}) {
  const [statsCochees, setStatsCochees] = React.useState(statsParDefaut);
  // Années décochées (vide = toutes affichées) : on ne connaît la liste des
  // années qu'une fois les données reçues.
  const [anneesDecochees, setAnneesDecochees] = React.useState([]);

  // La date est `date_affichage` côté eau ET côté irrigation dans la vraie
  // API (une version antérieure du contrat parlait d'`irr_affichage`) : on
  // tolère les deux plutôt que de tout perdre si le nom change.
  const lignes = (data ?? []).map((r) => ({
    ...r,
    __date: r[cleDate] ?? r.date_affichage ?? r.irr_affichage,
  }));

  // points[stat][annee] = [{ date, valeur }] (déjà cumulés si `cumul`).
  const points = {};
  STATS.forEach(({ cle }) => {
    const groupes = groupSeriesXY(lignes, {
      xKey: "__date",
      groupKey: "Years",
      valueKey: `${prefixe}_${cle}`,
    });
    points[cle] = {};
    groupes.forEach(({ group, points: pts }) => {
      const utiles = pts.filter((p) => p.y != null);
      const bornes = xMax != null ? utiles.filter((p) => toTimestamp(p.x) <= xMax) : utiles;
      let somme = 0;
      points[cle][group] = bornes.map((p) => {
        if (cumul) {
          somme += p.y;
          return { date: p.x, valeur: Math.round(somme * 10) / 10 };
        }
        return { date: p.x, valeur: p.y };
      });
    });
  });

  const annees = Object.keys(points.moy || {}).sort(
    (a, b) => (ORDRE_HORIZON[a] ?? 99) - (ORDRE_HORIZON[b] ?? 99)
  );
  const anneeBase = annees[0];

  // Mode écart : chaque point devient (valeur de l'année - valeur de l'année de
  // base pour la même statistique, à la même date). Les dates de l'année de
  // base sont interpolées si elles ne coïncident pas exactement.
  if (ecartAvecBase) {
    STATS.forEach(({ cle }) => {
      const base = points[cle][anneeBase] || [];
      annees
        .filter((a) => a !== anneeBase)
        .forEach((annee) => {
          points[cle][annee] = (points[cle][annee] || []).flatMap((p) => {
            const ref = valueAtDate(base, toTimestamp(p.date));
            return ref == null
              ? []
              : [{ date: p.date, valeur: Math.round((p.valeur - ref) * 100) / 100 }];
          });
        });
    });
  }

  // Années proposées / tracées : sans l'année de base en mode écart.
  const anneesAffichables = ecartAvecBase ? annees.filter((a) => a !== anneeBase) : annees;
  const statsAffichables = STATS.filter((st) => statsDisponibles.includes(st.cle));

  const basculerStat = (cle) =>
    setStatsCochees((actuelles) =>
      actuelles.includes(cle) ? actuelles.filter((c) => c !== cle) : [...actuelles, cle]
    );
  const basculerAnnee = (annee) =>
    setAnneesDecochees((actuelles) =>
      actuelles.includes(annee) ? actuelles.filter((a) => a !== annee) : [...actuelles, annee]
    );

  const series = [];
  anneesAffichables
    .filter((annee) => !anneesDecochees.includes(annee))
    .forEach((annee) => {
      const couleur = YEAR_COLORS[annee] || "#888";
      statsAffichables.filter((st) => statsCochees.includes(st.cle)).forEach((st) => {
        series.push({
          name: `${libelleAnnee(annee)} · ${st.label}`,
          type: "line",
          symbol: "none",
          lineStyle: { width: 2, color: couleur, type: st.echarts },
          itemStyle: { color: couleur },
          data: (points[st.cle][annee] || []).map((p) => [toTimestamp(p.date), p.valeur]),
        });
      });
    });

  // L'axe Y est calculé sur TOUTES les courbes (pas seulement les cochées),
  // pour qu'il ne saute pas à chaque clic dans la légende.
  const toutesValeurs = statsAffichables.flatMap(({ cle }) =>
    anneesAffichables.flatMap((annee) => (points[cle][annee] || []).map((p) => p.valeur))
  );
  const yMin =
    yMinAuto && toutesValeurs.length ? Math.floor(Math.min(...toutesValeurs)) : undefined;

  const option = {
    tooltip: {
      trigger: "axis",
      textStyle: { fontSize: 14, color: "#000" },
      formatter: (params) => {
        const date = formatDateFr(params[0].value[0]);
        let html = `${date}<br/>`;
        params.forEach((p) => {
          const v = Number(p.value[1]);
          const texte = `${ecartAvecBase && v > 0 ? "+" : ""}${v.toFixed(1)}`;
          html += `${p.marker} ${p.seriesName} : ${texte} ${unite}<br/>`;
        });
        return html;
      },
    },
    grid: { left: 50, right: 20, top: 50, bottom: 20, containLabel: true },
    xAxis: { type: "time", max: xMax ?? undefined, axisLabel: { formatter: formatDateFr } },
    yAxis: {
      type: "value",
      min: yMin,
      name: nomAxe,
      nameLocation: "end",
      nameGap: 15,
      nameTextStyle: { fontSize: 13, fontWeight: 600, color: "#000", align: "left" },
    },
    series,
  };

  // ---- Tableau : Q75 et Q90 pour chaque année ----
  const pointsBase = (stat) => points[stat]?.[anneeBase] || [];
  const moisCandidats = Array.from({ length: 12 }, (_, m) => new Date(2025, m, 1).getTime());
  const mois = [];
  moisCandidats.forEach((t, m) => {
    // On garde le mois si l'année de base a une valeur pour chaque statistique.
    const bases = STATS_TABLEAU.map((st) => valueAtDate(pointsBase(st), t));
    // En mode écart, la référence est le Moyen de l'année de base.
    const moyBase = valueAtDate(pointsBase("moy"), t);
    if (bases.every((b) => b != null) && (!tableauEnEcart || moyBase != null)) {
      mois.push({ m, t, bases, moyBase });
    }
  });
  const autresAnnees = annees.filter((a) => a !== anneeBase);
  const libelleStat = (cle) => STATS.find((st) => st.cle === cle).label;

  return (
    <div className="oad-pheno-layout" style={{ display: "flex", gap: 16, alignItems: "stretch" }}>
      <div style={{ flex: "3 1 0", display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div style={{ flex: "1 1 auto", minHeight: 350, display: "flex" }}>
          <ReactECharts
            option={option}
            style={{ height: "100%", minHeight: 350, width: "100%" }}
            notMerge
            lazyUpdate
          />
        </div>

        {/* Deux sélecteurs de légende : années, puis statistiques. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
          {selecteurAnnees && (
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#000" }}>Années :</span>
            {anneesAffichables.map((annee) => {
              const actif = !anneesDecochees.includes(annee);
              return (
                <Puce key={annee} actif={actif} onClick={() => basculerAnnee(annee)}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: actif ? YEAR_COLORS[annee] || "#888" : "#d0d4d9",
                    }}
                  />
                  {libelleAnnee(annee)}
                </Puce>
              );
            })}
          </div>
          )}

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#000" }}>Statistiques :</span>
            {statsAffichables.map((st) => {
              const actif = statsCochees.includes(st.cle);
              return (
                <Puce key={st.cle} actif={actif} onClick={() => basculerStat(st.cle)}>
                  <svg width="30" height="8" aria-hidden="true">
                    <line
                      x1="0"
                      y1="4"
                      x2="30"
                      y2="4"
                      stroke={actif ? "#000" : "#d0d4d9"}
                      strokeWidth="2"
                      strokeDasharray={motifSvg(st.echarts)}
                    />
                  </svg>
                  {st.label}
                </Puce>
              );
            })}
          </div>
        </div>
      </div>

      {/* Même flex que le tableau : le graphique garde sa largeur de 3/5. */}
      {!afficherTableau && reserverPlaceTableau && (
        <div aria-hidden="true" style={{ flex: "2 1 0" }} />
      )}

      {afficherTableau && mois.length > 0 && (
        <div style={{ flex: "2 1 0", overflowX: "auto" }}>
          <table className="oad-data-table oad-table-centered">
            <thead>
              <tr>
                <th rowSpan={2}></th>
                <th colSpan={STATS_TABLEAU.length}>{libelleAnnee(anneeBase)}</th>
                {autresAnnees.map((annee) => (
                  <th key={annee} colSpan={STATS_TABLEAU.length}>{libelleAnnee(annee)}</th>
                ))}
              </tr>
              <tr>
                {/* Année de base : valeurs absolues, l'unité est dans l'en-tête. */}
                {STATS_TABLEAU.map((st) => (
                  <th key={`base-${st}`}>{libelleStat(st)} ({unite})</th>
                ))}
                {autresAnnees.flatMap((annee) =>
                  STATS_TABLEAU.map((st) => <th key={`${annee}-${st}`}>{libelleStat(st)}</th>)
                )}
              </tr>
            </thead>
            <tbody>
              {mois.map(({ m, t, bases, moyBase }) => (
                <tr key={m}>
                  <td>{formatMonthLabel(m)}</td>
                  {bases.map((base, i) => (
                    <td key={`base-${i}`}>
                      {/* Tableau en écart (besoin en eau) : 1 décimale, comme les
                          colonnes d'écart ; sinon valeur entière. */}
                      {tableauEnEcart
                        ? base.toLocaleString("fr-FR", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })
                        : base.toFixed(0)}
                    </td>
                  ))}
                  {autresAnnees.flatMap((annee) =>
                    STATS_TABLEAU.map((st, i) => {
                      const val = valueAtDate(points[st]?.[annee] || [], t);
                      const base = bases[i];
                      if (tableauEnEcart) {
                        // Écart en valeur avec le Moyen de l'année de base :
                        // q75_2035 - moyen_2020 pour la colonne Quantile 75.
                        const ecart = val == null ? null : Math.round((val - moyBase) * 10) / 10;
                        return (
                          <td key={`${annee}-${st}`}>
                            {ecart == null
                              ? "—"
                              : `${ecart > 0 ? "+" : ""}${ecart.toLocaleString("fr-FR", {
                                  minimumFractionDigits: 1,
                                  maximumFractionDigits: 1,
                                })}`}
                          </td>
                        );
                      }
                      const v =
                        val == null || base === 0
                          ? null
                          : Math.round(((val - base) / base) * 1000) / 10;
                      return (
                        <td key={`${annee}-${st}`}>
                          {v == null ? "—" : `${v > 0 ? "+" : ""}${v.toLocaleString("fr-FR")} %`}
                        </td>
                      );
                    })
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {tableauEnEcart && (
            <p style={{ fontSize: 13, color: "#000", marginTop: 6 }}>
              {autresAnnees.map(libelleAnnee).join(" et ")} : écart avec le Moyen{" "}
              {libelleAnnee(anneeBase)} ({unite}).
            </p>
          )}
        </div>
      )}
    </div>
  );
}
